import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Card, CardContent, CardActions, Button, Grid,
  Chip, Box, Alert
} from '@mui/material';
import billingApi from '../services/billingApi';
import ReferralCard from '../components/ReferralCard';
import SubscriptionCardSkeleton from '../components/SubscriptionCardSkeleton';
import { formatDateShort as formatDate, subscriptionStatusColor as statusColor } from '../utils/formatters';

/**
 * Map workflow IDs to the subscription states they apply to.
 * This determines which action buttons appear on each subscription card.
 */
const WORKFLOW_STATE_MAP = {
  cancel_at_period_end: { statuses: ['active', 'trialing'], excludeIf: sub => !!sub.cancel_at_period_end || sub.status === 'admin_granted' },
  downgrade_subscription: { statuses: ['active', 'trialing'], excludeIf: sub => !!sub.cancel_at_period_end || sub.status === 'admin_granted' },
  pause_subscription: { statuses: ['active'], excludeIf: sub => !!sub.cancel_at_period_end },
  resume_subscription: { statuses: ['paused'] },
  reactivate_subscription: { statuses: ['active'], requireIf: sub => !!sub.cancel_at_period_end },
};

const WORKFLOW_BUTTON_STYLE = {
  cancel_at_period_end: { color: 'error', label: 'Cancel' },
  downgrade_subscription: { color: 'warning', label: 'Downgrade' },
  pause_subscription: { color: 'inherit', label: 'Pause' },
  resume_subscription: { color: 'primary', label: 'Resume' },
  reactivate_subscription: { color: 'primary', label: 'Reactivate' },
};

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [subData, wfData] = await Promise.all([
        billingApi.get('/api/subscriptions'),
        billingApi.get('/api/workflows'),
      ]);
      setSubscriptions(subData.subscriptions);
      setWorkflows(wfData.definitions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleManagePayment() {
    try {
      const data = await billingApi.post('/api/subscriptions/portal');
      window.location.href = data.portal_url;
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRetryPayment(subscriptionId) {
    setRetrying(subscriptionId);
    setError('');
    try {
      await billingApi.post(`/api/subscriptions/${subscriptionId}/retry-payment`);
      // Allow time for webhook processing before reloading
      setTimeout(loadData, 2000);
    } catch (err) {
      setError(err.data?.message || err.message || 'Payment retry failed');
    } finally {
      setRetrying(null);
    }
  }

  /**
   * Get the workflow-driven action buttons for a subscription.
   * Only shows buttons for customer_visible workflows that match
   * the subscription's current state.
   */
  function getActions(sub) {
    const actions = [];

    for (const wf of workflows) {
      const mapping = WORKFLOW_STATE_MAP[wf.workflowId];
      if (!mapping) continue;

      // Check if workflow applies to this subscription's state
      const statusMatch = mapping.statuses.includes(sub.status);
      const excluded = mapping.excludeIf?.(sub);
      const required = mapping.requireIf ? mapping.requireIf(sub) : true;

      if ((statusMatch || mapping.requireIf) && !excluded && required) {
        const style = WORKFLOW_BUTTON_STYLE[wf.workflowId] || { color: 'inherit' };
        actions.push({
          workflowId: wf.workflowId,
          label: style.label || wf.name,
          color: style.color,
          subscriptionId: sub.id,
        });
      }
    }

    return actions;
  }

  if (loading) {
    // Static header renders during loading too (disabled action) — only the
    // card list swaps when data arrives (standard rule 1, no layout jump).
    return (
      <Container maxWidth="md" sx={{ py: 4 }} aria-busy="true">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">My Subscriptions</Typography>
          <Button variant="outlined" disabled>
            Manage Payment Method
          </Button>
        </Box>
        <Grid container spacing={3}>
          {[0, 1].map((i) => (
            <Grid item xs={12} key={i}>
              <SubscriptionCardSkeleton />
            </Grid>
          ))}
        </Grid>
        <Box component="span" role="status" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          Loading subscriptions…
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">My Subscriptions</Typography>
        <Button variant="outlined" onClick={handleManagePayment}>
          Manage Payment Method
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {subscriptions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">No subscriptions yet</Typography>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/plans')}>
            Browse Plans
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {subscriptions
            .sort((a, b) => {
              const order = { active: 0, trialing: 0, admin_granted: 0, paused: 1, past_due: 1, canceled: 2, expired: 2 };
              return (order[a.status] ?? 3) - (order[b.status] ?? 3);
            })
            .map(sub => {
            const actions = getActions(sub);
            const isInactive = ['canceled', 'expired'].includes(sub.status);

            return (
              <Grid item xs={12} key={sub.id}>
                <Card sx={isInactive ? { opacity: 0.6 } : {}}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6">{sub.product_name}</Typography>
                        <Typography variant="body1" fontWeight={600}>{sub.plan_name}</Typography>
                        {sub.status === 'admin_granted' ? (
                          <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                            Complimentary access — no charge
                          </Typography>
                        ) : (() => {
                          if (sub.status === 'trialing') {
                            return (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {sub.price_cents > 0 ? `$${(sub.price_cents / 100).toFixed(2)}/${sub.interval_type === 'year' ? 'yr' : 'mo'} after trial` : 'Free plan'}
                              </Typography>
                            );
                          }
                          return sub.price_cents > 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              ${(sub.price_cents / 100).toFixed(2)}/{sub.interval_type === 'year' ? 'yr' : 'mo'}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Free plan
                            </Typography>
                          );
                        })()}
                      </Box>
                      <Chip
                        label={
                          sub.status === 'admin_granted' ? 'Enterprise' :
                          sub.status === 'trialing' ? 'Free Trial' :
                          sub.status === 'canceled' ? 'Canceled' :
                          sub.status === 'expired' ? 'Expired' :
                          sub.status === 'paused' ? 'Paused' :
                          sub.status.replace('_', ' ')
                        }
                        color={statusColor(sub.status)}
                        variant={sub.status === 'admin_granted' ? 'filled' : 'outlined'}
                      />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 3 }}>
                      {sub.current_period_end && (
                        <Typography variant="body2" color="text.secondary">
                          {sub.cancel_at_period_end ? 'Access until' : 'Next billing'}: {formatDate(sub.current_period_end)}
                        </Typography>
                      )}
                      {sub.status === 'admin_granted' && !sub.current_period_end && (
                        <Typography variant="body2" color="text.secondary">
                          No expiration
                        </Typography>
                      )}
                      {sub.status === 'paused' && (
                        <Typography variant="body2" color="text.secondary">
                          Subscription paused — billing suspended
                        </Typography>
                      )}
                    </Box>
                    {sub.status === 'past_due' && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Payment Failed
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {sub.last_payment_failure_reason || 'Your last payment could not be processed.'}
                        </Typography>
                        {sub.dunning_started_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {(() => {
                              const daysLeft = Math.max(0, 7 - Math.floor((Date.now() - new Date(sub.dunning_started_at).getTime()) / (1000 * 60 * 60 * 24)));
                              return `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining before cancellation`;
                            })()}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                          <Button size="small" variant="contained" color="error" onClick={handleManagePayment}>
                            Update Payment Method
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={retrying === sub.id}
                            onClick={() => handleRetryPayment(sub.id)}
                          >
                            {retrying === sub.id ? 'Retrying...' : 'Retry Payment Now'}
                          </Button>
                        </Box>
                      </Alert>
                    )}
                    {sub.status === 'canceled' && (
                      <Alert severity="error" icon={false} sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight={600}>Subscription canceled</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {sub.canceled_at ? `Canceled on ${formatDate(sub.canceled_at)}.` : ''}
                          {sub.ended_at ? ` Access ended ${formatDate(sub.ended_at)}.` :
                           sub.current_period_end ? ` Access until ${formatDate(sub.current_period_end)}.` : ''}
                          {sub.cancellation_reason ? ` Reason: ${sub.cancellation_reason}` : ''}
                        </Typography>
                      </Alert>
                    )}
                    {sub.status === 'expired' && (
                      <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Subscription expired
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Your subscription has expired.
                        </Typography>
                      </Alert>
                    )}
                    {!!sub.cancel_at_period_end && sub.status !== 'canceled' && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Your subscription will cancel at the end of the current billing period.
                      </Alert>
                    )}
                  </CardContent>

                  <CardActions>
                    {/* Workflow-driven action buttons */}
                    {actions.map(action => (
                      <Button
                        key={action.workflowId}
                        size="small"
                        color={action.color}
                        onClick={() => navigate(`/workflows/run/${action.workflowId}?subscription=${action.subscriptionId}`)}
                      >
                        {action.label}
                      </Button>
                    ))}

                    {['active', 'trialing'].includes(sub.status) && (
                      <Button size="small" onClick={() => navigate(`/subscriptions/${sub.id}`)}>
                        Details
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {subscriptions.length > 0 && <ReferralCard />}
    </Container>
  );
}
